import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

import plating from "../../../assets/admin/landing/plating.webp";
import { STEPS } from "./data";
import { EASE, Em, MaskLines, SectionTag, useIsDesktop } from "./primitives";

/* ------------------------------------------------------------------ */
/*  Foto die openklapt tot schermvullend, met het citaat erover        */
/* ------------------------------------------------------------------ */

export function Story() {
   const ref = useRef(null);
   const desktop = useIsDesktop();
   const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

   const from = desktop ? "inset(20% 32% 20% 32% round 28px)" : "inset(26% 10% 26% 10% round 24px)";
   const clip = useTransform(scrollYProgress, [0, 0.55], [from, "inset(0% 0% 0% 0% round 0px)"]);
   const scale = useTransform(scrollYProgress, [0, 0.55], [1.3, 1]);
   const wordsOut = useTransform(scrollYProgress, [0.02, 0.22], [1, 0]);
   const topY = useTransform(scrollYProgress, [0, 0.25], ["0%", "-120%"]);
   const bottomY = useTransform(scrollYProgress, [0, 0.25], ["0%", "120%"]);
   const quoteOpacity = useTransform(scrollYProgress, [0.5, 0.7], [0, 1]);
   const quoteY = useTransform(scrollYProgress, [0.5, 0.75], [60, 0]);

   return (
      <section ref={ref} aria-label="Ervaring van een klant" className="relative h-[260vh] bg-lp-paper">
         <div className="sticky top-0 h-screen overflow-hidden">
            <motion.p
               style={{ opacity: wordsOut, y: topY }}
               className="absolute inset-x-0 top-[9%] text-center text-[clamp(2.6rem,7vw,7rem)] font-semibold leading-none tracking-[-0.05em] text-lp-ink"
            >
               Van tafel
            </motion.p>
            <motion.p
               style={{ opacity: wordsOut, y: bottomY }}
               className="absolute inset-x-0 bottom-[9%] text-center text-[clamp(2.6rem,7vw,7rem)] leading-none tracking-[-0.03em] text-lp-blue"
            >
               <Em>tot telefoon.</Em>
            </motion.p>

            <motion.div style={{ clipPath: clip }} className="absolute inset-0 overflow-hidden bg-lp-ink">
               <motion.img
                  style={{ scale }}
                  src={plating}
                  alt="Een chef werkt een bord af met saus"
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover object-[50%_45%]"
               />
               <div className="absolute inset-0 bg-gradient-to-t from-lp-ink via-lp-ink/50 to-lp-ink/10" />
            </motion.div>

            <motion.figure
               style={{ opacity: quoteOpacity, y: quoteY }}
               className="absolute inset-x-0 bottom-0 mx-auto max-w-[1400px] px-5 pb-14 text-lp-paper sm:px-8 sm:pb-20"
            >
               <span className="font-editorial text-[7rem] leading-[0.5] text-lp-blue">“</span>
               <blockquote className="mt-2 max-w-4xl text-[clamp(1.6rem,3.6vw,3.2rem)] font-medium leading-[1.12] tracking-[-0.03em]">
                  Sinds we online bestellingen aanbieden via Resto-Online is onze afhaalomzet fors gestegen. Het
                  beheer kost me nu <Em className="text-lp-blue">bijna geen tijd</Em> meer.
               </blockquote>
               <figcaption className="mt-8 flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-lp-paper font-editorial text-xl italic text-lp-ink">
                     V
                  </span>
                  <span>
                     <span className="block font-medium">Vjeu-Djeu</span>
                     <span className="block font-ticket text-[11px] uppercase tracking-[0.16em] text-lp-paper/50">Restaurant · Antwerpen</span>
                  </span>
               </figcaption>
            </motion.figure>
         </div>
      </section>
   );
}

/* ------------------------------------------------------------------ */
/*  In drie stappen online                                             */
/* ------------------------------------------------------------------ */

export function Steps() {
   return (
      <section className="lp-grain relative bg-lp-paper px-5 py-28 sm:px-8 sm:py-40">
         <div className="mx-auto max-w-[1400px]">
            <SectionTag>Zo werkt het</SectionTag>
            <MaskLines
               as="h2"
               className="mt-8 text-[clamp(2.8rem,6.4vw,6rem)] font-semibold leading-[0.92] tracking-[-0.05em] text-lp-ink"
               lines={["Vandaag beslist, ", <Em className="text-lp-blue">vanavond online.</Em>]}
            />

            <ol className="mt-20 grid gap-12 md:grid-cols-3 md:gap-8">
               {STEPS.map((s, i) => (
                  <motion.li
                     key={s.k}
                     initial="off"
                     whileInView="on"
                     viewport={{ once: true, margin: "-15% 0px" }}
                     className="relative pt-8"
                  >
                     <motion.span
                        variants={{ off: { scaleX: 0 }, on: { scaleX: 1 } }}
                        transition={{ duration: 1.2, ease: EASE, delay: i * 0.25 }}
                        className="absolute left-0 right-0 top-0 h-px origin-left bg-lp-ink"
                     />
                     <motion.span
                        variants={{ off: { scale: 0 }, on: { scale: 1 } }}
                        transition={{ duration: 0.5, ease: EASE, delay: i * 0.25 + 0.2 }}
                        className="absolute -top-[5px] left-0 h-[11px] w-[11px] rounded-full bg-lp-blue"
                     />
                     <motion.div
                        variants={{ off: { opacity: 0, y: 30 }, on: { opacity: 1, y: 0 } }}
                        transition={{ duration: 0.9, ease: EASE, delay: i * 0.25 + 0.15 }}
                     >
                        <div className="flex items-baseline justify-between">
                           <span className="font-editorial text-[5.5rem] italic leading-none text-lp-ink">{s.k}</span>
                           <span className="font-ticket text-[11px] uppercase tracking-[0.18em] text-lp-muted">{s.time}</span>
                        </div>
                        <h3 className="mt-6 text-2xl font-semibold tracking-[-0.03em] text-lp-ink">{s.title}</h3>
                        <p className="mt-3 max-w-sm text-[16px] leading-relaxed text-lp-muted">{s.body}</p>
                     </motion.div>
                  </motion.li>
               ))}
            </ol>
         </div>
      </section>
   );
}
